import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PosAuthService } from '@adminpages/pos-api';
import { PosStoreService } from '@adminpages/pos-state';
import { loginRouteDecision, sessionDestination } from './session-routing';

export const entryGuard: CanActivateFn = () => {
  const auth = inject(PosAuthService);
  const store = inject(PosStoreService);
  const router = inject(Router);

  return router.parseUrl(sessionDestination(sessionRouteState(auth, store)));
};

export const loginGuard: CanActivateFn = () => {
  const auth = inject(PosAuthService);
  const store = inject(PosStoreService);
  const router = inject(Router);
  const decision = loginRouteDecision(sessionRouteState(auth, store));

  return decision === true ? true : router.parseUrl(decision);
};

export const authGuard: CanActivateFn = () => {
  const auth = inject(PosAuthService);
  const store = inject(PosStoreService);
  const router = inject(Router);

  return hasRestoredSession(auth, store) || router.parseUrl('/login');
};
export const posGuard: CanActivateFn = () => capabilityGuard('pos');
export const repairsGuard: CanActivateFn = () => capabilityGuard('repairs');
export const cashGuard: CanActivateFn = () => capabilityGuard('cash');

function capabilityGuard(capability: 'pos' | 'repairs' | 'cash'): boolean | ReturnType<Router['parseUrl']> {
  const auth = inject(PosAuthService);
  const store = inject(PosStoreService);
  const router = inject(Router);

  if (!hasRestoredSession(auth, store)) {
    return router.parseUrl('/login');
  }

  const workspace = store.selectedWorkspace();

  if (!store.cashRegisterReady()) {
    return router.parseUrl('/cash-register');
  }

  return workspace.capabilities[capability] || router.parseUrl(workspace.homeRoute);
}

function sessionRouteState(auth: PosAuthService, store: PosStoreService) {
  return {
    hasToken: Boolean(auth.token()),
    hasWorkspace: hasRestoredWorkspace(store),
    entryRoute: store.entryRoute(),
  };
}

function hasRestoredSession(auth: PosAuthService, store: PosStoreService): boolean {
  return Boolean(auth.token()) && hasRestoredWorkspace(store);
}

function hasRestoredWorkspace(store: PosStoreService): boolean {
  return store.workspaces().some((workspace) => workspace.id === store.selectedWorkspace().id);
}
